/**
 * 내선 관리 화면 조작 공통 흐름 — 중복 제출 방지, 성공·경고 토스트, 재조회, 실패 문구.
 *
 * - 조작은 멱등 키가 없다: 같은 키의 조작이 진행 중이면 다시 부르지 않는다(버튼은
 *   isPending 으로 비활성). 4단계 createCounselorNumber 와 같은 이유.
 * - 응답 warnings 가 있으면 성공 토스트 대신 경고/오류 토스트(`요청을 접수했습니다` 접두).
 * - 실패 문구는 서버 한국어 message 패스스루(parseCallApiError). 화면 동작이 달라지는
 *   코드(pbxErrorReaction)는 호출부 onError 가 먼저 가로챌 수 있고, `refetch` 류는 여기서
 *   메시지 뒤 재조회까지 한다.
 * - 조작이 진행 중인 동안 `inventory.setActionPending(true)` 로 알려 조건부 폴링이 그 틱을
 *   건너뛰게 한다(조작 직후 재조회와 겹치지 않게). 진행 중인 키가 모두 끝나면 false.
 * - 응답이 오기 전에 병원(`inventory.companyId`)이 바뀌었으면 그 응답은 버린다 — 토스트·재조회·
 *   반환값 없이 undefined. 호출부가 공유 목록(`replaceExtensions`, `sites.items`)을 직접 고치므로
 *   이전 병원의 응답이 새 병원 목록에 얹히지 않게 한다(요청 문서 "요청 계약 2").
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 공통"
 */
import { message } from 'ant-design-vue';
import { ref } from 'vue';

import { parseCallApiError, pbxErrorReaction } from '@/views/Callbot/service/callApiError';
import { createWarningToast } from '@/views/Callbot/service/pbxExtensions';

/**
 * @typedef {Object} PbxActionOptions
 * @property {string | null | ((res: any) => string | null)} success
 *   성공 토스트. null 이면 띄우지 않는다(결과 모달이 성공 표시인 조작)
 * @property {Array<'extensions' | 'devices' | 'sites' | 'counselorNumbers'>=} reload
 *   성공 후 재조회할 목록. 기본 없음
 * @property {string} fallback 실패 문구 fallback(서버 message 가 없을 때)
 * @property {((err: unknown, text: string) => boolean | void)=} onError
 *   실패를 호출부가 먼저 본다 — true 를 돌려주면 공통 처리(오류 토스트)를 건너뛴다.
 *   모달 안 조작은 여기서 인라인 오류로 돌리고 true 를 돌려준다.
 */

export const PBX_ACTION_DONE_PREFIX = '요청을 접수했습니다';

/**
 * @param {ReturnType<import('./usePbxInventory').usePbxInventory>} inventory
 */
export function usePbxAction(inventory) {
  const pending = ref(new Set());

  /** @param {string} key */
  function isPending(key) {
    return pending.value.has(key);
  }

  function setPending(key, on) {
    const next = new Set(pending.value);
    if (on) next.add(key);
    else next.delete(key);
    pending.value = next;
  }

  /** 조작을 시작한 뒤 병원이 바뀌었는지 — 바뀌었으면 그 응답은 버린다 */
  function companyChanged(company) {
    return inventory.companyId?.value !== company;
  }

  /**
   * 조작 실행. 성공이면 응답을, 실패·중복·병원 전환이면 undefined 를 돌려준다.
   * @template T
   * @param {string} key
   * @param {() => Promise<T>} fn
   * @param {PbxActionOptions} opts
   * @returns {Promise<T | undefined>}
   */
  async function run(key, fn, opts) {
    if (isPending(key)) return undefined;
    setPending(key, true);
    inventory.setActionPending?.(true);
    const company = inventory.companyId?.value;
    try {
      const res = await fn();
      if (companyChanged(company)) return undefined;
      const warnings = res?.warnings;
      const warn = createWarningToast(warnings, { done: PBX_ACTION_DONE_PREFIX });
      if (warn) {
        if (warn.kind === 'error') message.error(warn.text);
        else message.warning(warn.text);
      } else {
        const text = typeof opts.success === 'function' ? opts.success(res) : opts.success;
        if (text) message.success(text);
      }
      if (opts.reload?.length) void inventory.reload(opts.reload);
      return res;
    } catch (err) {
      if (companyChanged(company)) return undefined;
      const text = parseCallApiError(err, opts.fallback);
      const handled = opts.onError?.(err, text);
      if (!handled) message.error(text);
      if (pbxErrorReaction(err) === 'refetch') void inventory.reload();
      return undefined;
    } finally {
      setPending(key, false);
      if (pending.value.size === 0) inventory.setActionPending?.(false);
    }
  }

  return { run, isPending };
}

/** 1회 표시 값(URL·비밀번호·복구 코드) 복사 — 실패하면 직접 선택해 복사하라고 안내한다 */
export async function copyPbxText(text) {
  try {
    await navigator.clipboard.writeText(text);
    message.success('복사했습니다.');
  } catch {
    message.error('복사하지 못했습니다. 값을 직접 선택해 복사해주세요.');
  }
}
